import { X, Check, Sparkles, Star, Shuffle, Download, Zap, Crown } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PLANS = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    period: "/forever",
    features: {
      scripts_per_day: "5",
      tone_selection: false,
      category_selection: false,
      favorites: false,
      variations: false,
      ai_tools: false,
      export: false,
      long_form: false,
    },
    highlight: false,
    color: "zinc",
  },
  {
    id: "basic",
    name: "Basic",
    price: "$4.99",
    period: "/month",
    features: {
      scripts_per_day: "5",
      tone_selection: true,
      category_selection: true,
      favorites: false,
      variations: false,
      ai_tools: false,
      export: false,
      long_form: false,
    },
    highlight: false,
    color: "zinc",
  },
  {
    id: "pro",
    name: "Pro",
    price: "$7.99",
    period: "/month",
    features: {
      scripts_per_day: "10",
      tone_selection: true,
      category_selection: true,
      favorites: true,
      variations: true,
      ai_tools: false,
      export: false,
      long_form: false,
    },
    highlight: true,
    badge: "Most Popular",
    color: "violet",
  },
  {
    id: "unlimited",
    name: "Unlimited",
    price: "$14.99",
    period: "/month",
    features: {
      scripts_per_day: "Unlimited",
      tone_selection: true,
      category_selection: true,
      favorites: true,
      variations: true,
      ai_tools: true,
      export: true,
      long_form: true,
    },
    highlight: false,
    color: "amber",
  },
];

const FEATURE_LABELS = {
  scripts_per_day: { label: "Scripts per day", icon: Sparkles },
  tone_selection: { label: "Tone selection", icon: Zap },
  category_selection: { label: "Category selection", icon: Zap },
  favorites: { label: "Save favorites", icon: Star },
  variations: { label: "Generate variations", icon: Shuffle },
  ai_tools: { label: "AI tool prompts", icon: Crown },
  export: { label: "Export PDF/TXT", icon: Download },
  long_form: { label: "Long-form scripts (20-60s)", icon: Crown },
};

// Map features to minimum required plan
const FEATURE_REQUIRED_PLAN = {
  tone_selection: "basic",
  category_selection: "basic",
  favorites: "pro",
  variations: "pro",
  ai_tools: "unlimited",
  export: "unlimited",
  long_form: "unlimited",
};

export function UpgradeModal({ isOpen, onClose, feature, currentPlan }) {
  const navigate = useNavigate();
  
  if (!isOpen) return null;
  
  const requiredPlan = FEATURE_REQUIRED_PLAN[feature] || "basic";
  const featureInfo = FEATURE_LABELS[feature];
  const FeatureIcon = featureInfo?.icon || Sparkles;
  
  // Find the recommended plan based on the feature
  const recommendedPlan = PLANS.find(p => p.id === requiredPlan);
  
  const handleUpgrade = () => {
    onClose();
    navigate("/pricing");
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 animate-fade-in"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div 
          className="bg-gradient-to-br from-zinc-900/95 via-zinc-900/90 to-zinc-800/80 backdrop-blur-xl border border-zinc-700/50 rounded-2xl w-full max-w-3xl my-8 animate-scale-in shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative px-6 pt-6 pb-4 border-b border-zinc-800/50">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-800/50 transition-colors"
            >
              <X className="w-5 h-5 text-zinc-400" />
            </button>
            
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-violet-500/20 backdrop-blur-sm border border-violet-500/20 flex items-center justify-center">
                <FeatureIcon className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Unlock {featureInfo?.label || "this feature"}</h2>
                <p className="text-sm text-zinc-400">
                  Upgrade to {recommendedPlan?.name} or higher to access this feature
                </p>
              </div>
            </div>
          </div>

          {/* Plans Comparison */}
          <div className="p-6">
            <div className="grid grid-cols-4 gap-3">
              {PLANS.map((plan) => {
                const isCurrentPlan = plan.id === currentPlan;
                const isRecommended = plan.id === requiredPlan;
                const hasFeature = plan.features[feature] === true || 
                  (typeof plan.features[feature] === "string" && plan.features[feature] !== "0");
                
                return (
                  <div
                    key={plan.id}
                    className={`relative rounded-xl p-4 transition-all ${
                      isRecommended
                        ? "bg-violet-500/10 border-2 border-violet-500/50 ring-2 ring-violet-500/20"
                        : isCurrentPlan
                        ? "bg-zinc-800/50 border border-zinc-700"
                        : "bg-zinc-800/30 border border-zinc-800"
                    }`}
                  >
                    {/* Badges */}
                    {isRecommended && (
                      <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-violet-500 text-white text-[10px] font-bold rounded-full uppercase tracking-wider">
                        Recommended
                      </div>
                    )}
                    {isCurrentPlan && !isRecommended && (
                      <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-zinc-600 text-white text-[10px] font-bold rounded-full uppercase tracking-wider">
                        Current
                      </div>
                    )}
                    
                    {/* Plan Info */}
                    <div className="text-center mb-3">
                      <h3 className={`font-bold ${isRecommended ? "text-violet-300" : "text-white"}`}>
                        {plan.name}
                      </h3>
                      <div className="mt-1">
                        <span className={`text-2xl font-bold ${isRecommended ? "text-violet-400" : "text-white"}`}>
                          {plan.price}
                        </span>
                        <span className="text-xs text-zinc-500">{plan.period}</span>
                      </div>
                    </div>
                    
                    {/* Feature Check */}
                    <div className={`flex items-center justify-center gap-2 py-2 px-3 rounded-lg ${
                      hasFeature 
                        ? "bg-green-500/10 text-green-400" 
                        : "bg-zinc-900/50 text-zinc-600"
                    }`}>
                      {hasFeature ? (
                        <>
                          <Check className="w-4 h-4" />
                          <span className="text-xs font-medium">Included</span>
                        </>
                      ) : (
                        <>
                          <X className="w-4 h-4" />
                          <span className="text-xs font-medium">Not included</span>
                        </>
                      )}
                    </div>
                    
                    {/* Scripts per day */}
                    <div className="mt-3 text-center">
                      <span className="text-xs text-zinc-500">Scripts/day:</span>
                      <span className={`ml-1 text-xs font-bold ${isRecommended ? "text-violet-300" : "text-white"}`}>
                        {plan.features.scripts_per_day}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Full Feature Comparison */}
            <div className="mt-6 pt-6 border-t border-zinc-800">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">
                Full Feature Comparison
              </h3>
              <div className="space-y-2">
                {Object.entries(FEATURE_LABELS).map(([key, { label, icon: Icon }]) => (
                  <div key={key} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-zinc-800/30">
                    <Icon className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                    <span className={`text-sm flex-1 ${key === feature ? "text-violet-300 font-medium" : "text-zinc-400"}`}>
                      {label}
                      {key === feature && (
                        <span className="ml-2 text-[10px] bg-violet-500/20 text-violet-400 px-1.5 py-0.5 rounded">
                          This feature
                        </span>
                      )}
                    </span>
                    <div className="flex gap-6">
                      {PLANS.map((plan) => {
                        const value = plan.features[key];
                        const hasIt = value === true || (typeof value === "string" && value !== "0");
                        return (
                          <div key={plan.id} className="w-16 text-center">
                            {typeof value === "string" ? (
                              <span className="text-xs font-medium text-zinc-300">{value}</span>
                            ) : hasIt ? (
                              <Check className="w-4 h-4 text-green-400 mx-auto" />
                            ) : (
                              <X className="w-4 h-4 text-zinc-600 mx-auto" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Plan headers for the comparison table */}
              <div className="flex items-center gap-3 py-2 px-3 mt-2">
                <div className="w-4 flex-shrink-0" />
                <div className="flex-1" />
                <div className="flex gap-6">
                  {PLANS.map((plan) => (
                    <div key={plan.id} className="w-16 text-center">
                      <span className={`text-[10px] font-bold uppercase tracking-wider ${
                        plan.id === requiredPlan ? "text-violet-400" : "text-zinc-500"
                      }`}>
                        {plan.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer CTA */}
          <div className="px-6 py-4 border-t border-zinc-800 bg-zinc-900/50 rounded-b-2xl">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm text-zinc-400">
                Ready to unlock more features?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-zinc-400 hover:text-white transition-colors"
                >
                  Maybe later
                </button>
                <button
                  onClick={handleUpgrade}
                  className="px-6 py-2 bg-violet-500 hover:bg-violet-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  View Plans
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default UpgradeModal;
